import React, { useState, useContext } from 'react';
import { AccountIcon, HeartIcon, OrderIcon, CalendarIcon } from '../components/icons';
import { useAuth } from '../context/AuthContext';
import { useAllOrders } from '../context/AllOrdersContext';
import { useData } from '../context/DataContext';
import { AppContext } from '../context/AppContext';
import { wilayas } from '../data/mockData';
import RestaurantCard from '../components/RestaurantCard';
import DriverRatingModal from '../components/DriverRatingModal';
import LiveTrackingMap from '../components/LiveTrackingMap';
import { Reservation } from '../types';
import { db } from '../firebase/client';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { formatDateReadable } from '../utils/dateUtils';

const AccountScreen: React.FC = () => {
  const { currentUser, updateUserProfile, logout, requestNotificationPermission, notificationPermission } = useAuth();
  const { placedOrders, loading, cancelOrder, updateOrderStatus } = useAllOrders();
  const { restaurants } = useData();
  const { setView } = useContext(AppContext);
  const [userReservations, setUserReservations] = useState<Reservation[]>([]);
  const [showReservations, setShowReservations] = useState(false);
  const [loadingRes, setLoadingRes] = useState(false);
  const [activeTab, setActiveTab] = useState<'sent' | 'completed' | 'cancelled' | null>(null);
  const [ratingOrder, setRatingOrder] = useState<typeof placedOrders[0] | null>(null);
  const [trackingOrderId, setTrackingOrderId] = useState<string | null>(null);
  const [showFavorites, setShowFavorites] = useState(false);

  React.useEffect(() => {
    if (!currentUser?.uid || !showReservations) return;

    setLoadingRes(true);
    const q = query(
        collection(db, 'reservations'),
        where('clientId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
        const resData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Reservation));
        
        // Sort manually to avoid composite index requirement
        resData.sort((a: any, b: any) => {
            const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 
                         (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : 0);
            const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 
                         (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : 0);
            return timeB - timeA;
        });

        setUserReservations(resData.slice(0, 3));
        setLoadingRes(false);
    }, (error) => {
        console.error("Error fetching user reservations:", error);
        setLoadingRes(false);
    });

    return () => unsubscribe();
  }, [currentUser?.uid, showReservations]);
  const [editedName, setEditedName] = useState(currentUser?.name || '');
  const [editedPhone, setEditedPhone] = useState(currentUser?.phone || '');
  const [editedWilaya, setEditedWilaya] = useState(currentUser?.wilaya || '');
  const [editedAddress, setEditedAddress] = useState(currentUser?.address || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Sync with currentUser when it loads
  React.useEffect(() => {
    if (currentUser) {
      if (!editedName) setEditedName(currentUser.name || '');
      if (!editedPhone) setEditedPhone(currentUser.phone || '');
      if (!editedWilaya) setEditedWilaya(currentUser.wilaya || '');
      if (!editedAddress) setEditedAddress(currentUser.address || '');
    }
  }, [currentUser]);

  const hasChanges = editedName !== (currentUser?.name || '') ||
    editedPhone !== (currentUser?.phone || '') ||
    editedWilaya !== (currentUser?.wilaya || '') ||
    editedAddress !== (currentUser?.address || '');

  const handleSaveProfile = async () => {
    if (!hasChanges) return;
    setIsUpdating(true);
    setUpdateSuccess(false);
    try {
      await updateUserProfile({
        name: editedName,
        phone: editedPhone,
        wilaya: editedWilaya,
        address: editedAddress
      });
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (err: any) {
      alert("Erreur lors de la sauvegarde.");
    } finally {
      setIsUpdating(false);
    }
  };

  const favoritesList = restaurants.filter(r => r.isFavorite);

  // Group orders for history section
  const historySent = placedOrders.filter(o => ['Envoyée', 'Prête', 'Livrée'].includes(o.status));
  const historyCompleted = placedOrders.filter(o => o.status === 'Terminée');
  const historyCancelled = placedOrders.filter(o => ['Annulée', 'Refusée'].includes(o.status));

  const renderOrderList = (orders: typeof placedOrders) => {
    if (orders.length === 0) {
      return (
        <div className="py-12 flex flex-col items-center justify-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200 animate-fade-in mb-6">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
            <OrderIcon className="w-6 h-6 text-gray-300" />
          </div>
          <p className="text-gray-400 font-bold text-[10px] uppercase tracking-[0.2em] italic">Aucune commande</p>
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {orders.map(order => {
          const isRegisteredDriver = order.livreurId && !order.livreurId.startsWith('manual_');
          
          return (
            <div key={order.id} className="border-t pt-4 first:border-t-0">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-bold">Commande #{order.id.replace('ord_', '')}</p>
                  {order.customerAddress && (
                    <p className="text-[10px] text-gray-500 font-medium flex items-center gap-1 mt-0.5">
                       <span>📍</span> {order.customerAddress}
                    </p>
                  )}
                </div>
                {/* Utilisation de new Date() car stored as string in localStorage */}
                <p className="text-gray-400 text-xs mb-1">
                    {formatDateReadable(order.timestamp)}
                </p>
              </div>
              {/* Statuts et Actions */}
              <div className="flex flex-col items-end gap-2">
                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${order.status === 'En attente' ? 'bg-yellow-200 text-yellow-800' :
                  order.status === 'En préparation' ? 'bg-blue-200 text-blue-800' :
                    order.status === 'Envoyée' ? 'bg-green-200 text-green-800' :
                      order.status === 'Livrée' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'Annulée' ? 'bg-gray-200 text-gray-800' : 'bg-red-200 text-red-800'
                  }`}>{order.status}</span>

                {order.status === 'En attente' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Voulez-vous vraiment annuler cette commande ?')) {
                        cancelOrder(order.id);
                      }
                    }}
                    className="px-3 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 hover:bg-red-200 transition-colors"
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>

            {order.status === 'En préparation' && order.preparationTime && (
              <div className="mt-2 bg-amber-50 border border-amber-100 rounded-xl p-2 flex items-center gap-2">
                <span className="text-sm">⏳</span>
                <p className="text-xs font-bold text-amber-800">Prêt dans environ <span className="text-amber-600">{order.preparationTime} min</span></p>
              </div>
            )}

            {/* Détail Livraison & Livreur */}
            <div className="mt-3 pt-3 border-t border-gray-50">
              <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
                <span>Frais de livraison</span>
                <span>{order.deliveryFee > 0 ? `${order.deliveryFee} DA` : 'Offerte'}</span>
              </div>

              {/* Affichage du Livreur si la commande est envoyée ou livrée */}
              {['Envoyée', 'Livrée'].includes(order.status) && order.assignedDriver && (
                <div className="bg-green-50 p-2 rounded-lg flex items-center justify-between xl-2 border border-green-100">
                  <div>
                    <p className="text-xs font-bold text-green-800 uppercase tracking-wide">Votre Livreur</p>
                    <p className="text-sm font-semibold text-gray-800">{order.assignedDriver.name}</p>
                  </div>
                  <div className="flex gap-2">
                    {order.assignedDriver.phone && (
                      <a href={`tel:${order.assignedDriver.phone}`} className="flex items-center gap-1 bg-white px-3 py-1.5 rounded-full shadow-sm text-green-700 font-bold text-xs hover:bg-green-100 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                        </svg>
                        {order.assignedDriver.phone}
                      </a>
                    )}
                    {order.status === 'Envoyée' && isRegisteredDriver && (
                      <button onClick={(e) => {
                        e.stopPropagation();
                        setTrackingOrderId(trackingOrderId === order.id ? null : order.id);
                      }} className={`p-1.5 rounded-full shadow-sm transition-colors ${trackingOrderId === order.id ? 'bg-green-600 text-white' : 'bg-white text-green-600 hover:bg-green-50'}`}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Live Tracking Map Inline */}
            {order.status === 'Envoyée' && trackingOrderId === order.id && (
              <div className="mt-3 animate-fade-in-up">
                <LiveTrackingMap orderId={order.id} clientLocation={order.customerLocation} livreurId={order.livreurId} />
              </div>
            )}

            <div className="flex justify-between items-center mt-1">
              <span className="text-xs text-gray-400">Total</span>
              {/* Prix en Rouge */}
              <p className="text-right font-bold text-red-700 text-lg">{order.totalPrice} DA</p>
            </div>
            
            {(order.status === 'Livrée' || (order.status === 'Envoyée' && !isRegisteredDriver && order.assignedDriver)) && (
              <div className="mt-3 pt-3 border-t border-gray-50 flex justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isRegisteredDriver) {
                      if (window.confirm('Confirmer la réception de votre commande ?')) {
                        updateOrderStatus(order.id, 'Terminée');
                      }
                    } else {
                      setRatingOrder(order);
                    }
                  }}
                  className="px-4 py-2 bg-black text-white text-xs font-bold rounded-lg hover:bg-gray-800 transition-colors shadow-sm"
                >
                  {!isRegisteredDriver ? 'Reçu' : 'Confirmer la réception'}
                </button>
              </div>
            )}
            </div>
          )})}
      </div>
    );
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic mt-4 mb-8">Mon Compte</h1>

      <div className="bg-white p-6 rounded-2xl shadow-md mb-6 flex items-center justify-between">
        <div className="flex items-center w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mr-4">
            <AccountIcon className="w-10 h-10 text-red-600" />
          </div>
          <div className="flex-1">
            <input
              type="text"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              className="text-xl font-bold bg-transparent border-b border-transparent hover:border-gray-200 focus:border-red-500 outline-none w-full"
            />
            <p className="text-gray-600">{currentUser?.email}</p>
          </div>
        </div>
      </div>

      {/* Section Notifications */}
      <div className="bg-white p-6 rounded-2xl shadow-md mb-6 transition-all hover:shadow-lg">
        <h3 className="text-xl font-black text-gray-900 mb-4 flex items-center tracking-tighter uppercase italic">
          <span className="bg-blue-100 p-2 rounded-full mr-2 not-italic">🔔</span>
          Notifications <span className="text-red-600 ml-1">Push</span>
        </h3>
        <div className="flex items-center justify-between p-4 bg-blue-50/50 rounded-xl border border-blue-100">
          <div className="flex-1 mr-4">
            <p className="font-bold text-gray-800">
              {notificationPermission === 'granted'
                ? "Notifications Activées ✅"
                : "Activer les notifications"}
            </p>
            <p className="text-xs text-gray-600 mt-1">
              {notificationPermission === 'granted'
                ? "Vous recevrez des alertes en temps réel sur l'état de vos commandes."
                : "Autorisez les notifications pour être informé du suivi de vos commandes."}
            </p>
          </div>
          {notificationPermission !== 'granted' && (
            <button
              onClick={requestNotificationPermission}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold text-sm shadow-sm hover:bg-blue-700 transition-colors active:scale-95"
            >
              Autoriser
            </button>
          )}
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl shadow-md mb-6">
        <h3 className="text-xl font-black text-gray-900 mb-4 flex items-center tracking-tighter uppercase italic">
          <span className="bg-red-100 p-2 rounded-full mr-2 not-italic">📞</span>
          Mes <span className="text-red-600 ml-1">Coordonnées</span>
        </h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Numéro de téléphone</label>
            <input
              type="tel"
              placeholder="05 / 06 / 07 ..."
              value={editedPhone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                setEditedPhone(val);
              }}
              maxLength={10}
              className="w-full p-3 mt-1 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none bg-gray-50 font-semibold"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Ma Wilaya</label>
            <select
              value={editedWilaya}
              onChange={(e) => setEditedWilaya(e.target.value)}
              className="w-full p-3 mt-1 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none bg-gray-50 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%236b7280%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.5rem_center] bg-no-repeat pr-10 font-semibold"
            >
              <option value="">Choisir une ville...</option>
              {wilayas.map((w: string) => (
                <option key={w} value={w}>{w}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="text-sm font-medium text-gray-700">Adresse Complète</label>
              {currentUser?.location && (
                <button 
                  onClick={async () => {
                    if (window.confirm("Voulez-vous réinitialiser vos coordonnées GPS ? Votre adresse texte sera conservée.")) {
                      setIsUpdating(true);
                      try {
                        await updateUserProfile({ location: null as any });
                        alert("Position réinitialisée.");
                      } catch (e) {
                        alert("Erreur.");
                      } finally {
                        setIsUpdating(false);
                      }
                    }
                  }}
                  className="text-[10px] font-black text-red-600 bg-red-50 px-2 py-1 rounded-lg uppercase tracking-wider"
                >
                  Réinitialiser GPS
                </button>
              )}
            </div>
            <textarea
              placeholder="Ex: Cité 500 logements, Bâtiment B, N°12..."
              value={editedAddress}
              onChange={(e) => setEditedAddress(e.target.value)}
              rows={2}
              className="w-full p-3 mt-1 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 outline-none bg-gray-50 font-semibold resize-none"
            />
            {currentUser?.location && (
              <p className="text-[10px] text-green-600 font-bold mt-1 flex items-center gap-1">
                <span>📍</span> Position GPS enregistrée
              </p>
            )}
          </div>

          <div className="pt-2">
            <button
              onClick={handleSaveProfile}
              disabled={!hasChanges || isUpdating}
              className={`w-full py-3 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 ${hasChanges
                ? 'bg-red-600 text-white hover:bg-red-700 active:scale-[0.98]'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
            >
              {isUpdating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Enregistrement...
                </>
              ) : updateSuccess ? (
                <> ✅ Enregistré ! </>
              ) : (
                "Sauvegarder les modifications"
              )}
            </button>
          </div>

          <p className="text-xs text-gray-500 italic">Ces informations nous permettent de personnaliser votre expérience sur Yumii.</p>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <div
          className="bg-white p-4 rounded-xl shadow-sm flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setShowReservations(!showReservations)}
        >
          <div className="flex items-center">
            <CalendarIcon className="w-6 h-6 text-red-600 mr-4" />
            <span className="text-lg font-black text-gray-900 tracking-tighter uppercase italic">Mes Réservations ({userReservations.length})</span>
          </div>
          <span>{showReservations ? '▲' : '▼'}</span>
        </div>

        {showReservations && (
          <div className="space-y-3 mt-2 animate-fade-in-up">
            {loadingRes ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-gray-200 border-t-red-600 rounded-full animate-spin" />
              </div>
            ) : userReservations.length > 0 ? (
              userReservations.map(res => (
                <div key={res.id} className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-black text-gray-900 uppercase italic tracking-tighter text-sm">{res.restaurantName}</h4>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{formatDateReadable(res.date)} à {res.time}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
                      res.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                      res.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {res.status === 'confirmed' ? 'Confirmée' : res.status === 'pending' ? 'En attente' : 'Refusée'}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-[10px] font-bold text-gray-500 uppercase">
                    <div className="flex items-center gap-1.5">
                      <span className="opacity-50">👥</span>
                      {res.guests} pers.
                    </div>
                    {res.specialRequests && (
                      <div className="flex items-center gap-1.5 flex-1 line-clamp-1 italic text-gray-400">
                        <span className="opacity-50">💬</span>
                        "{res.specialRequests}"
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="py-10 flex flex-col items-center justify-center bg-white rounded-2xl border border-dashed border-gray-200">
                <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest italic">Aucune réservation</p>
              </div>
            )}
          </div>
        )}

        <div
          className="bg-white p-4 rounded-xl shadow-sm flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setShowFavorites(!showFavorites)}
        >
          <div className="flex items-center">
            <HeartIcon className="w-6 h-6 text-red-500 mr-4" />
            <span className="text-lg font-black text-gray-900 tracking-tighter uppercase italic">Mes restaurants favoris ({favoritesList.length})</span>
          </div>
          <span>{showFavorites ? '▲' : '▼'}</span>
        </div>

        {showFavorites && (
          <div className="grid grid-cols-2 gap-3 mt-2 px-1 animate-fade-in-up">
            {favoritesList.length > 0 ? (
              favoritesList.map(restaurant => (
                <div key={restaurant.id} className="w-full">
                  <RestaurantCard
                    restaurant={restaurant}
                    onClick={() => setView({ screen: 'restaurantDetail', params: { id: restaurant.id } })}
                  />
                </div>
              ))
            ) : (
              <div className="col-span-2 py-10 flex flex-col items-center justify-center bg-white rounded-2xl border border-gray-100 shadow-sm transition-all animate-fade-in">
                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                  <HeartIcon className="w-8 h-8 text-red-200" />
                </div>
                <p className="text-gray-400 font-bold text-xs uppercase tracking-widest italic text-center px-6 leading-relaxed">
                  Vous n'avez pas encore de restaurants favoris. 
                  <br/>
                  <span className="text-[10px] lowercase font-medium">Cliquez sur le coeur pour en ajouter !</span>
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-12 text-center">
        <button onClick={logout} className="text-red-600 font-semibold border border-red-200 px-6 py-2 rounded-full hover:bg-red-50">
          Se déconnecter
        </button>
      </div>

      {
        ratingOrder && (
          <DriverRatingModal
            order={ratingOrder}
            onClose={() => setRatingOrder(null)}
            onSuccess={async () => {
              await updateOrderStatus(ratingOrder.id, 'Terminée');
              setRatingOrder(null);
            }}
          />
        )
      }
    </div >
  );
};

export default AccountScreen;